import { type ButtonHTMLAttributes, forwardRef } from 'react';
import { cn } from '@doccareplus/shared/lib/utils';
import { LoadingSpinner } from './LoadingSpinner';

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  /** Visual style variant */
  variant?: 'primary' | 'ghost' | 'outline' | 'danger' | 'ghost-sm';
  size?: 'sm' | 'md' | 'lg';
  isLoading?: boolean;
  fullWidth?: boolean;
}

/**
 * Button — Pure presentational component.
 * Variants mirror the design system from docs/unbundle-design/Medrecordly.dc.html
 *
 * NO business logic — only styling and event forwarding.
 */
export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant = 'primary',
      size = 'md',
      isLoading = false,
      fullWidth = false,
      disabled,
      children,
      ...props
    },
    ref
  ) => {
    const base =
      'inline-flex items-center justify-center font-medium ' +
      'transition-[transform,box-shadow,background-color,border-color,color] duration-200 ease-[cubic-bezier(0.2,0.8,0.2,1)] ' +
      'active:scale-[0.975] ' +
      'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ring)] ' +
      'focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50';

    const variants: Record<NonNullable<ButtonProps['variant']>, string> = {
      primary:
        'bg-[var(--primary)] text-[var(--primary-fg)] hover:bg-[var(--primary-hover)] ' +
        'rounded-[9px] shadow-[var(--shadow-1)] hover:shadow-[var(--shadow-2)] ' +
        'border border-transparent active:shadow-none',
      ghost:
        'border border-[var(--border)] bg-[var(--surface)] text-[var(--text-2)] ' +
        'hover:bg-[var(--surface-2)] hover:text-[var(--text)] hover:border-[var(--border-strong)] rounded-[9px] active:bg-[var(--surface-3)]',
      outline:
        'border border-[var(--border)] bg-transparent text-[var(--text-2)] ' +
        'hover:bg-[var(--surface-2)] hover:text-[var(--text)] hover:border-[var(--border-strong)] rounded-[9px] active:bg-[var(--surface-2)]',
      danger:
        'bg-[var(--danger-solid)] text-white hover:bg-[var(--danger-strong)] ' +
        'rounded-[9px] shadow-[var(--shadow-1)] hover:shadow-[var(--shadow-2)] active:shadow-none',
      'ghost-sm':
        'border border-[var(--border)] bg-[var(--surface)] text-[var(--text-2)] ' +
        'hover:bg-[var(--surface-2)] hover:text-[var(--text)] hover:border-[var(--border-strong)] rounded-[8px] active:bg-[var(--surface-3)]',
    };

    const sizes: Record<NonNullable<ButtonProps['size']>, string> = {
      sm: 'h-8 px-3 text-[12px] gap-1.5',
      md: 'h-[38px] px-[15px] text-[13.5px] gap-2',
      lg: 'h-[42px] px-5 text-[14.5px] gap-2',
    };

    // ghost-sm uses its own size
    const sizeClass =
      variant === 'ghost-sm' ? 'h-[30px] px-[10px] text-[12.5px] gap-1.5' : sizes[size];

    return (
      <button
        ref={ref}
        className={cn(base, variants[variant], sizeClass, fullWidth && 'w-full', className)}
        disabled={disabled || isLoading}
        {...props}
      >
        {isLoading && <LoadingSpinner size="sm" className="spinner-vitality-button text-current" />}
        {children}
      </button>
    );
  }
);

Button.displayName = 'Button';
